import { t } from "../../localization";
import { MeasureType } from "../../types/measurements";
import { Units } from "../../types/project";
import { isLonLatProjection } from "../projections";
import { MeasurementVolume, StationMeasurement } from "./measurement";

export class MeasurementFormatter {
  public units: Units;
  public emptyValue = "";
  private metersCutoff = 500;
  private squareMetersCutoff = 250000;
  private precision = 2; // 0.01m
  private vectorPrecisionLonLat = 6; // ~0.01m
  private vectorPrecisionStandard = 2; // 0.01m

  constructor(units: Units) {
    const allowedUnits = Object.values(Units);

    if (!allowedUnits.includes(units)) {
      throw TypeError(`Units must be ${allowedUnits}`);
    }

    this.setUnits(units);
  }

  get nullValue() {
    return t("general.not-applicable");
  }

  get isMetric() {
    return this.units === Units.Meters;
  }

  get isImperial() {
    return (this.units === Units.Feet) || (this.units === Units.SurveyFeet);
  }

  get isSurveyFeet() {
    return this.units === Units.SurveyFeet;
  }

  get areaUnits() {
    return `${this.isMetric ? "m" : "ft"}^2`;
  }

  get volumeUnits() {
    return `${this.isMetric ? "m" : "yd"}^3`;
  }

  setUnits(units: Units) {
    this.units = units;
  }

  formatLength(value: number) {
    let units;
    let finalValue = value;

    if ((this.units === Units.Feet) || (this.units === Units.SurveyFeet)) {
      if (value > this.metersCutoff) {
        units = "mi";
        finalValue = MeasurementFormatter.metresToMiles(finalValue, this.isSurveyFeet);
      } else {
        // Round the meters units first for feet + inches
        finalValue = this.roundFloat(finalValue);
        finalValue = MeasurementFormatter.metresToFeet(finalValue, this.isSurveyFeet);
        let feet = Math.floor(finalValue);
        let inches = Math.round((finalValue - feet) * 12.0);
        return `${feet} ft ${inches} in `;
      }
    } else {
      if (value > this.metersCutoff) {
        units = "km";
        finalValue =  MeasurementFormatter.metresToKiloMetres(finalValue);
      } else {
        units = "m";
      }
    }

    finalValue = this.roundFloat(finalValue);

    return `${finalValue} ${units}`;
  }

  formatVolume(measurement: MeasurementVolume) {
    let units;
    let value = measurement.volume;

    if ((this.units === Units.Feet) || (this.units === Units.SurveyFeet)) {
      units = "yd<sup>3</sup>";
      value = MeasurementFormatter.cubicMetresToCubicYards(value);
    } else {
      units = "m<sup>3</sup>";
    }

    value = this.roundFloat(value);

    return `${value} ${units}`;
  }

  formatArea(value: number) {
    let units;
    let finalValue = value;

    if ((this.units === Units.Feet) || (this.units === Units.SurveyFeet)) {
      if (value > this.squareMetersCutoff) {
        units = "mi<sup>2</sup>";
        finalValue = MeasurementFormatter.squareMetresToSquareMiles(
          finalValue, this.isSurveyFeet);
      } else {
        units = "ft<sup>2</sup>";
        finalValue = MeasurementFormatter.squareMetresToSquareFeet(
          finalValue, this.isSurveyFeet);
      }
    } else {
      if (value > this.squareMetersCutoff) {
        units = "km<sup>2</sup>";
        finalValue =  MeasurementFormatter.squareMetresToSquareKilometres(
          finalValue);
      } else {
        units = "m<sup>2</sup>";
      }
    }

    finalValue = this.roundFloat(finalValue);

    return `${finalValue} ${units}`;
  }

  formatAngle(value: number, format = true) {
    if (!isNaN(value)) {
      value = this.roundFloat(value);
      return format ? `${value}°` : value;
    }

    return this.nullValue;
  }

  formatGrade(value: number, format = true) {
    let maxGrade = 1000000;
    if (isFinite(value) && (value < maxGrade)) {
      value = this.roundFloat(value);
      return format ? `${value}%` : value;
    }

    return this.nullValue;
  }

  formatStation(measurement: StationMeasurement, format=true) {
    const along = measurement.distanceAlong;
    const start = measurement.staStart;
    const lengthUnits = measurement.units;

    let alongFinal = along;
    let stationFinal = start;

    if (this.isImperial) {
      if (lengthUnits === Units.Meters) {
        alongFinal = MeasurementFormatter.metresToFeet(alongFinal, this.isSurveyFeet);
        stationFinal = MeasurementFormatter.metresToFeet(stationFinal, this.isSurveyFeet);
      }

    } else {
      if (lengthUnits === Units.SurveyFeet) {
        stationFinal = MeasurementFormatter.feetToMetres(stationFinal, true);
        alongFinal = MeasurementFormatter.feetToMetres(alongFinal, true);
      } else if (lengthUnits === Units.Feet){
        stationFinal = MeasurementFormatter.feetToMetres(stationFinal, false);
        alongFinal = MeasurementFormatter.feetToMetres(alongFinal, false);
      }
    }

    const stationTotal = stationFinal + alongFinal;

    let stationNumber: number;
    let stationAlong: number;

    if (this.isImperial) {
      stationNumber = parseInt(String(stationTotal).slice(0,4));
      stationAlong = parseFloat(String(stationTotal).slice(4));
    } else {
      stationNumber = parseInt(String(stationTotal).slice(0,3));
      stationAlong = parseFloat(String(stationTotal).slice(3));
    }

    stationAlong = this.roundFloat(stationAlong);

    return format
      ? `${stationNumber} + ${stationAlong} ${this.units}`
      : `${stationNumber} + ${stationAlong}`;
  }

  formatOffset(measurement: StationMeasurement, format=true) {
    const across = measurement.distanceAcross;
    const side = measurement.sideOfSegment;
    let finalValue = (side === "R") ? across : -across;
    const lengthUnits = measurement.units;

    if (this.isImperial) {
      if (lengthUnits === Units.Meters) {
        finalValue = MeasurementFormatter.metresToFeet(finalValue, this.isSurveyFeet);
      }
    } else {
      if (lengthUnits === Units.SurveyFeet) {
        finalValue = MeasurementFormatter.feetToMetres(finalValue, true);
      } else if (lengthUnits === Units.Feet){
        finalValue = MeasurementFormatter.feetToMetres(finalValue, false);
      }
    }

    finalValue = this.roundFloat(finalValue);

    return format
      ? `${finalValue} ${this.units}`
      : `${finalValue}`;
  }

  convertUnits(value: number, type: MeasureType) {
    if ((this.units === Units.Feet) || (this.units === Units.SurveyFeet)) {
      if (type === MeasureType.Area) {
        value = MeasurementFormatter.squareMetresToSquareFeet(
          value, this.isSurveyFeet);
      } else if (type === MeasureType.Volume) {
        value = MeasurementFormatter.cubicMetresToCubicYards(value);
      } else {
        value = MeasurementFormatter.metresToFeet(value, this.isSurveyFeet);
      }
    }

    value = this.roundFloat(value);

    return value;
  }

  getVectorPrecision(projection = null) {
    let precisionX = this.vectorPrecisionStandard;
    let precisionY = this.vectorPrecisionStandard;
    let precisionZ = this.vectorPrecisionStandard;

    if (projection && isLonLatProjection(projection)) {
      precisionX = this.vectorPrecisionLonLat;
      precisionY = this.vectorPrecisionLonLat;
    }

    return {
      x: precisionX,
      y: precisionY,
      z: precisionZ
    };
  }

  roundFloat(value: number) {
    return parseFloat(value.toFixed(this.precision));
  }

  static metresToYards(value) {
    /** International units and US units are the same for yards */
    return value * (1 / 0.9144);
  }

  static metresToFeet(value, isSurveryFeet=false) {
    if (isSurveryFeet) {
      return value * (3937.0 / 1200.0);
    }

    return value * (1 / 0.3048);
  }

  static feetToMetres(value, isSurveryFeet=false) {
    if (isSurveryFeet) {
      return value * (1200.0 / 3937.0);
    }

    return value * (0.3048);
  }

  static metresToMiles(value, isSurveryFeet=false) {
    return value * this.metresToFeet(1.0, isSurveryFeet) / 5280;
  }

  static metresToKiloMetres(metres) {
    return metres * 0.001;
  }

  static squareMetresToSquareKilometres(squareMetres) {
    return squareMetres * 0.000001;
  }

  static squareMetresToSquareFeet(squareMetres, isSurveryFeet=false) {
    let val = Math.pow(this.metresToFeet(1.0, isSurveryFeet), 2);
    return squareMetres * val;
  }

  static squareMetresToSquareMiles(squareMetres, isSurveryFeet=false) {
    let val = Math.pow(this.metresToMiles(1.0, isSurveryFeet), 2);
    return squareMetres * val;
  }

  static cubicMetresToCubicYards(cubicMetres) {
    let val = Math.pow(this.metresToYards(1.0), 3);
    return cubicMetres * val;
  }
}


